import * as React from 'react';
import { cn } from '../lib/cn.js';

/**
 * Presentational table shell. The kit's data table renders through these, but a
 * bespoke screen can use them directly without pulling in TanStack Table.
 *
 * Row height and cell padding come from the density variables, so `compact` is a
 * real mode rather than a smaller zoom.
 */
export function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom border-collapse text-body', className)}
        {...props}
      />
    </div>
  );
}

export function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b [&_tr]:border-border', className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

export function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t border-border bg-surface-sunken font-medium', className)}
      {...props}
    />
  );
}

export function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b border-hairline transition-colors hover:bg-surface-sunken',
        'data-[state=selected]:bg-surface-selected',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-[var(--control-height)] px-[var(--cell-padding-x)] text-left align-middle',
        'text-label uppercase tracking-[0.08em] text-faint whitespace-nowrap',
        '[&:has([role=checkbox])]:w-0 [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'h-[var(--row-height)] px-[var(--cell-padding-x)] align-middle text-ink-2',
        '[&:has([role=checkbox])]:w-0 [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-secondary text-muted', className)}
      {...props}
    />
  );
}
